/**
 * Uso de pantallas por usuario — base del inicio personalizado (fase 1).
 *
 * App.js registra cada visita a una pantalla del módulo principal; el Inicio
 * usa esos datos para ordenar los accesos rápidos por frecuencia y mostrar
 * una fila de "recientes". Se guarda en localStorage NAMESPACED POR USUARIO
 * porque las tablets se comparten entre usuarios; sin usuario no se registra.
 *
 * La entrada guarda nombre/icono/keys tal como estaban en routes.js al
 * momento de la visita, para que el Inicio no tenga que importar routes.js
 * (evita el ciclo routes.js → dashboard → routes.js). Las `keys` se
 * re-filtran contra el rol vigente al mostrar, igual que el menú.
 */

const PREFIJO = "uso-pantallas:";
const MAX_VISITAS = 80; // pantallas distintas retenidas por usuario
const MAX_RECIENTES = 8;

function claveDe(usuario) {
  const u = String(usuario || "").trim();
  return u ? PREFIJO + u : null;
}

export function obtenerUso(usuario) {
  const clave = claveDe(usuario);
  if (!clave) return { visitas: {}, recientes: [] };
  try {
    const crudo = JSON.parse(localStorage.getItem(clave)) || {};
    return {
      visitas: crudo.visitas && typeof crudo.visitas === "object" ? crudo.visitas : {},
      recientes: Array.isArray(crudo.recientes) ? crudo.recientes : [],
    };
  } catch (e) {
    return { visitas: {}, recientes: [] };
  }
}

/** entrada: { ruta, nombre, icono, keys } (icono = nombre de Material Icon). */
export function registrarVisita(usuario, entrada) {
  const clave = claveDe(usuario);
  if (!clave || !entrada?.ruta) return;
  const uso = obtenerUso(usuario);

  const previa = uso.visitas[entrada.ruta];
  uso.visitas[entrada.ruta] = { n: (previa?.n || 0) + 1, t: Date.now() };

  // Si se pasa del tope, se descartan las pantallas sin visitar hace más tiempo.
  const rutas = Object.keys(uso.visitas);
  if (rutas.length > MAX_VISITAS) {
    rutas
      .sort((a, b) => (uso.visitas[a].t || 0) - (uso.visitas[b].t || 0))
      .slice(0, rutas.length - MAX_VISITAS)
      .forEach((r) => delete uso.visitas[r]);
  }

  uso.recientes = [
    { ruta: entrada.ruta, nombre: entrada.nombre, icono: entrada.icono, keys: entrada.keys, t: Date.now() },
    ...uso.recientes.filter((r) => r?.ruta !== entrada.ruta),
  ].slice(0, MAX_RECIENTES);

  try {
    localStorage.setItem(clave, JSON.stringify(uso));
  } catch (e) {
    // localStorage lleno o bloqueado: el inicio simplemente queda sin personalizar.
  }
}

/**
 * Aplana un arreglo estilo routes.js a un Map ruta → { ruta, nombre, icono,
 * keys }. Los hijos sin icono propio heredan el del grupo. `keys` replica la
 * resolución de menuVisible: keys explícitas, o programa ?? key.
 */
export function catalogoDesdeRutas(rutas) {
  const mapa = new Map();
  const recorrer = (lista, iconoPadre) => {
    (lista || []).forEach((r) => {
      const icono = typeof r?.icon?.props?.children === "string" ? r.icon.props.children : iconoPadre;
      if (Array.isArray(r?.collapse)) {
        recorrer(r.collapse, icono);
        return;
      }
      if (!r?.route || !r?.name) return;
      const keys = Array.isArray(r.keys) && r.keys.length ? r.keys : [r.programa ?? r.key];
      mapa.set(r.route, { ruta: r.route, nombre: r.name, icono: icono || "apps", keys });
    });
  };
  recorrer(rutas, null);
  return mapa;
}
